import React, {useContext, useEffect} from "react";
import {Box, Container, Stack} from "@mui/material";
import WorkClassSelectionStep from "../components/steps/WorkClassSelectionStep";
import SimulationSettingsStep from "../components/steps/SimulationSettingsStep";
import WorkInstanceSelectionStep from "../components/steps/WorkInstanceSelectionStep";
import SearchCriteriaAdditionalStep from "../components/steps/SearchCriteriaAdditionalStep";
import EquipmentConfigurationStep from "../components/steps/EquipmentConfigurationStep";
import CustomWorldSelectionStep from "../components/steps/CustomWorldSelectionStep";
import {
  BaseWorldPayload,
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  SearchCriteriaPayload,
  STEP_ORDERS,
  STEPS,
  useStep,
  WorkClassPayload
} from "../contexts/OperatingTestContext";
import useWorkClass from "../hooks/useWorkClass";
import {STATUSES, useFetchAllQueryHelper} from "../../../hooks/useFetchAllQuery";
import SearchForm from "../components/forms/SearchForm";
import CalcHandLoading from "../utils/CalcHandLoading";
import {useCalcHandCandidate} from "../hooks/useCalcHandCandidate";
import Pjdb1Button from "../../../components/inputs/Pjdb1Button";
import SelectedModels from "../components/lists/SelectedModels";
import {useBaseWorld} from "../hooks/useBaseWorld";
import BaseWorldSelectionStep from "../components/steps/BaseWorldSelectionStep";
import {ErrorBoundary} from "../../../components/ErrorBoundary";
import StepError from "../components/errors/StepError";
import {convertToNumber, } from "../../../utils/numberUtil";
import {omit} from "../../../utils/objectUtil";

export default function Simulation() {
  const {dispatch} = useContext(DataStoreContext);
  const [step, setStep] = useStep();

  useEffect(() => {
    const reset = () => {
      setStep(STEPS.WORK_CLASS_SELECTION);
      dispatch({type: CONTEXT_ACTION_TYPE.RESET, payload: {}});
    };
    window.addEventListener('beforeunload', reset);

    return () => {
      window.removeEventListener('beforeunload', reset);
    }
  }, [dispatch, setStep])

  return (
    <Container sx={{pt: 4}}>
      <ErrorBoundary fallback={<StepError/>}>
        {([STEPS.WORK_CLASS_SELECTION, STEPS.WORK_INSTANCE_SELECTION, STEPS.SEARCH_CRITERIA_ADDITIONAL] as string[]).includes(step) && (
          <SearchCriteriaSteps/>
        )}
        {step === STEPS.EQUIPMENT_CONFIGURATION && (
          <EquipmentConfigurationStep/>
        )}
        {([STEPS.BASE_WORLD_SELECTION, STEPS.CUSTOM_WORLD_SELECTION] as string[]).includes(step) && (
          <WorldSteps/>
        )}
        {step === STEPS.SIMULATION_SETTINGS && (
          <SimulationSettingsStep/>
        )}
      </ErrorBoundary>
    </Container>

  );
}

function SearchCriteriaSteps() {
  const {state, dispatch} = useContext(DataStoreContext);
  const [step, setStep] = useStep();
  const {data, status, fetchAll} = useWorkClass({keyword: state.workClassStep.searchCond.keyword});
  useFetchAllQueryHelper({
    data,
    status,
    initialStatus: state.workClassStep.searchStatus,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.WORK_CLASS,
        payload: {
          workClasses: newData,
          searchStatus: newStatus,
        } satisfies WorkClassPayload
      });
    }
  });
  const {isLoading, status: calcStatus, mutate} = useCalcHandCandidate({
    enableConfirm: false,
    conditions: {
      instances: state.searchCriteriaStep.searchCriteriaList.map((item) => ({
        ...omit(item, 'direction'),
        rangeOpen: convertToNumber(item.rangeOpen, 0),
        rangeClose: convertToNumber(item.rangeClose, 0),
        stroke: convertToNumber(item.stroke, 0),
        forceMin: convertToNumber(item.forceMin, 0),
        forceMax: convertToNumber(item.forceMax, undefined),
        payload: convertToNumber(item.payload, 0),
        flexibleWidth: convertToNumber(item.flexibleWidth, undefined),
      })),
      handPowerSource: state.searchCriteriaStep.handPowerSourceInputs.handPowerSource,
    },
    validate: () => {
      if (state.searchCriteriaStep.selectedArm === null) {
        return "アーム選択を選択してください。";
      }
      return true;
    },
    onSuccess: (result) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
        payload: {
          calculationResults: result,
        } as SearchCriteriaPayload
      });
      setStep(STEP_ORDERS[STEPS.SEARCH_CRITERIA_ADDITIONAL].NEXT);
    }
  });

  return (
    <Stack gap={4}>
      <SearchForm
        label="対象物名、JAN/部品コード、メーカーなどを入力"
        isLoading={state.workClassStep.searchStatus === STATUSES.FETCHING}
        searchCond={state.workClassStep.searchCond}
        onChange={(searchCond) => {
          dispatch({
            type: CONTEXT_ACTION_TYPE.WORK_CLASS,
            payload: {
              searchCond,
            } satisfies WorkClassPayload
          });
        }}
        onSubmit={(e) => {
          e.preventDefault();
          fetchAll().catch(console.error);
          setStep(STEPS.WORK_CLASS_SELECTION);
        }}
      />

      {step === STEPS.WORK_CLASS_SELECTION && <WorkClassSelectionStep/>}
      {step === STEPS.WORK_INSTANCE_SELECTION && <WorkInstanceSelectionStep/>}
      {step === STEPS.SEARCH_CRITERIA_ADDITIONAL && <SearchCriteriaAdditionalStep/>}

      <Stack gap={2} mt={2} width='100%'>
        <SelectedModels
          searchCriteriaList={state.searchCriteriaStep.searchCriteriaList}
          onDelete={(index) => {
            dispatch({
              type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
              payload: {
                searchCriteriaList: state.searchCriteriaStep.searchCriteriaList.filter((_, i) => i !== index),
              }
            });
          }}/>

        <Box display='flex' justifyContent='end'>
          <Pjdb1Button
            label='計算実行'
            disabled={state.searchCriteriaStep.searchCriteriaList.length <= 0}
            onClick={() => {
              mutate().catch(console.error)
            }}
            variant='contained'
            color='info'
          />
        </Box>

        <CalcHandLoading
          open={isLoading}
          status={calcStatus}
        />
      </Stack>
    </Stack>
  );
}

function WorldSteps() {
  const {state, dispatch} = useContext(DataStoreContext);
  const [step, setStep] = useStep();
  const {fetchAll: fetchBaseWorld} = useBaseWorld({
    searchCond: state.baseWorldStep.searchCond,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.BASE_WORLD,
        payload: {
          baseWorlds: newData,
          searchStatus: newStatus,
        } satisfies BaseWorldPayload
      });
    },
    initialStatus: state.baseWorldStep.searchStatus,
  });
  return (
    <Stack gap={4}>
      <SearchForm
        label="ベースワールド名などを入力"
        isLoading={state.baseWorldStep.searchStatus === STATUSES.FETCHING}
        searchCond={state.baseWorldStep.searchCond}
        onChange={(searchCond) => {
          dispatch({type: CONTEXT_ACTION_TYPE.BASE_WORLD, payload: {searchCond} satisfies BaseWorldPayload});
        }}
        onSubmit={(e) => {
          e.preventDefault();
          fetchBaseWorld().catch(console.error);
          setStep(STEPS.BASE_WORLD_SELECTION);
        }}
      />

      {step === STEPS.BASE_WORLD_SELECTION && (<BaseWorldSelectionStep/>)}
      {step === STEPS.CUSTOM_WORLD_SELECTION && (<CustomWorldSelectionStep/>)}
    </Stack>
  )
}
